<template>
  <Spinner class="self-center" v-if="loading" :loading="loading" />
  <section
    class="flex flex-col lg:flex-row"
    v-else
    data-id="smtp-imap-access-card"
    :data-inbox-id-scope="inboxIdScope"
  >
    <section v-if="!hasUserAccess" class="w-full flex flex-row justify-center items-center">
      <Btn
        tag="button"
        icon="refresh"
        size="sm"
        :variant="Variant.primary"
        @click="onRefreshRequest"
      >
        Enable SMTP/IMAP access
      </Btn>
    </section>
    <section class="flex-1" v-else>
      <CardBody>
        <FormGrouping>
          <FormGrouping>
            <FormGroup v-if="!inboxId" label="Inbox access" data-id="inboxes">
              <InboxSelect
                v-if="inboxes"
                data-id="inbox-select"
                :allow-all-inboxes="!inboxId"
                :default-inbox-id="inboxIdSelected || inboxId"
                :disabled="inboxId"
                @change="onInboxChange"
              />
            </FormGroup>
            <FormGroup :label="'Email address'" data-id="email">
              <FormInputRead
                size="sm"
                data-id="email-value"
                :value="emailAddress || ''"
              />
            </FormGroup>
          </FormGrouping>
          <FormGrouping lg-grid>
            <FormGrouping>
              <FormGroup :label="'IMAP TLS/SSL'" data-id="imap-secure">
                <FormCheckbox
                  v-model="secureImap"
                  data-id="toggle-secure-imap"
                  :switch="true"
                />
              </FormGroup>
              <FormGroup
                data-id="imap-mailbox"
                label="IMAP Mailbox"
                notes="The mailbox to use the IMAP SELECT command with after login"
              >
                <FormInputRead
                  data-id="imap-mailbox-value"
                  :value="imapMailbox || ''"
                  size="sm"
                />
              </FormGroup>

              <FormGroup :label="'IMAP Server Host'" data-id="imap-host">
                <FormInputRead
                  data-id="imap-host-value"
                  :value="imapHost || ''"
                  size="sm"
                />
              </FormGroup>
              <FormGroup :label="'IMAP Server Port'" data-id="imap-port">
                <FormInputRead
                  :value="imapPort || ''"
                  data-id="imap-port-value"
                  size="sm"
                />
              </FormGroup>

              <FormGroup :label="'IMAP Username'" data-id="imap-username">
                <FormInputRead
                  :value="imapUsername || ''"
                  :editable="true"
                  data-id="imap-username-value"
                  size="sm"
                  @change="onImapUsernameChange"
                />
              </FormGroup>
              <FormGroup :label="'IMAP Password'" data-id="imap-password">
                <FormInputRead
                  data-id="imap-password-value"
                  :editable="true"
                  :value="imapPassword || ''"
                  size="sm"
                  @change="onImapPasswordChange"
                />
              </FormGroup>
            </FormGrouping>
            <FormGrouping>
              <!-- smtp -->
              <FormGroup :label="'SMTP TLS/SSL'" data-id="smtp-secure">
                <FormCheckbox
                  v-model="secureSmtp"
                  data-id="toggle-secure-smtp"
                  :switch="true"
                />
              </FormGroup>
              <FormGroup data-id="smtp-mailfrom" label="MailFrom domain">
                <FormInputRead
                  v-if="smtpMailFrom"
                  size="sm"
                  data-id="smtp-mailfrom-value"
                  :value="smtpMailFrom"
                />
                <FormInputRead
                  v-else
                  size="sm"
                  data-id="smtp-mailfrom-null"
                  :value="' '"
                />
              </FormGroup>

              <FormGroup :label="'SMTP Server Host'" data-id="smtp-host">
                <FormInputRead
                  size="sm"
                  data-id="smtp-host-value"
                  :value="smtpHost || ''"
                />
              </FormGroup>
              <FormGroup :label="'SMTP Server Port'" data-id="smtp-port">
                <FormInputRead
                  size="sm"
                  data-id="smtp-port-value"
                  :value="smtpPort || ''"
                />
              </FormGroup>

              <FormGroup :label="'SMTP Username'" data-id="smtp-username">
                <FormInputRead
                  size="sm"
                  :editable="true"
                  data-id="smtp-username-value"
                  :value="smtpUsername || ''"
                  @change="onSmtpUsernameChange"
                />
              </FormGroup>
              <FormGroup data-id="smtp-password" :label="'SMTP Password'">
                <FormInputRead
                  size="sm"
                  data-id="smtp-password-value"
                  :editable="true"
                  :value="smtpPassword || ''"
                  @change="onSmtpPasswordChange"
                />
              </FormGroup>
            </FormGrouping>
          </FormGrouping>
        </FormGrouping>
      </CardBody>
    </section>
  </section>
</template>

<script setup lang="ts">
import FormCheckbox from '../../components/form/FormCheckbox.vue';
import FormGroup from '../../components/form/FormGroup.vue';
import CardBody from '../../components/tw/CardBody.vue';
import {
  loadFetch,
  loadFetchMany,
  loadFetchSeq,
} from '../../lib/services/helpers';
import Spinner from '../../components/Spinner.vue';
import { computed, watch, onMounted, ref } from '#imports';
import FormGrouping from '../../components/form/FormGrouping.vue';
import Btn from '../../components/form/Btn.vue';
import { useStore3 } from '../../store/main';
import { Variant } from '../../store/types';
import { storeToRefs } from 'pinia';
import FormInputRead from '../../components/form/FormInputRead.vue';
import InboxSelect from '../select/InboxSelect.vue';
import { getLogger } from '../../lib/services/getLogger';

// TODO download button
const log = getLogger('SMTPIMAPAccess');
const store3 = useStore3();

const props = withDefaults(
  defineProps<{
    inboxId?: string | undefined;
  }>(),
  { inboxId: undefined },
);

const loading = ref<boolean>(false);
const secureSmtp = ref<boolean>(false);
const inboxIdSelected = ref<string | null>('');
const secureImap = ref<boolean>(false);
const { user, inboxes, imapSmtpAccess } = storeToRefs(store3);

const hasUserAccess = computed(() => {
  return (
    user.value?.smtpUsername &&
    user.value?.smtpPassword &&
    user.value?.imapUsername &&
    user.value?.imapPassword
  );
});

const onInboxChange = async (i: string | null) => {
  log('On inbox change ' + i);
  if (i === 'ALL_INBOXES') {
    inboxIdSelected.value = null;
  } else {
    inboxIdSelected.value = i;
  }
};

const emailAddress = computed(() => {
  return imapSmtpAccess.value?.emailAddress;
});
const smtpMailFrom = computed(() => {
  return imapSmtpAccess.value?.mailFromDomain;
});
const smtpPort = computed(() => {
  return secureSmtp.value
    ? imapSmtpAccess.value?.secureSmtpServerPort?.toString(10)
    : imapSmtpAccess.value?.smtpServerPort?.toString(10);
});

const smtpHost = computed(() => {
  return secureSmtp.value
    ? imapSmtpAccess.value?.secureSmtpServerHost
    : imapSmtpAccess.value?.smtpServerHost;
});

const smtpUsername = computed(() => {
  return secureSmtp.value
    ? imapSmtpAccess.value?.secureSmtpUsername
    : imapSmtpAccess.value?.smtpUsername;
});
const smtpPassword = computed(() => {
  return secureSmtp.value
    ? imapSmtpAccess.value?.secureSmtpPassword
    : imapSmtpAccess.value?.smtpPassword;
});

const imapHost = computed(() => {
  return secureImap.value
    ? imapSmtpAccess.value?.secureImapServerHost
    : imapSmtpAccess.value?.imapServerHost;
});

const imapMailbox = computed(() => {
  return imapSmtpAccess.value?.imapMailbox;
});

const imapPassword = computed(() => {
  return secureImap.value
    ? imapSmtpAccess.value?.secureImapPassword
    : imapSmtpAccess.value?.imapPassword;
});

const imapUsername = computed(() => {
  return secureImap.value
    ? imapSmtpAccess.value?.secureImapUsername
    : imapSmtpAccess.value?.imapUsername;
});

const imapPort = computed(() => {
  return secureImap.value
    ? imapSmtpAccess.value?.secureImapServerPort?.toString(10)
    : imapSmtpAccess.value?.imapServerPort?.toString(10);
});

const inboxIdScope = computed(() => {
  return inboxIdSelected.value ?? props.inboxId;
});

onMounted(async () => {
  if (props.inboxId) {
    inboxIdSelected.value = props.inboxId;
  }
  await refresh();
});

const refresh = async () => {
  log('Refresh SMTP IMAP');
  if (hasUserAccess.value) {
    await loadFetchMany(
      [
        () => store3.getInboxes(),
        () => store3.getImapSmtpAccess({ inboxId: inboxIdScope.value }),
      ],
      store3,
      loading,
    );
  }
};

const onRefreshRequest = async () => {
  if (
    confirm(
      'Are you sure you want to reset your username and password for this connection?',
    )
  ) {
    log('Refreshing IMAP/SMTP access');
    await loadFetchSeq(
      [
        () => store3.refreshImapSmtpSecrets(),
        () => store3.getImapSmtpAccess({ inboxId: inboxIdScope.value }),
      ],
      store3,
      loading,
    );
  }
};
const onSmtpPasswordChange = async (newValue: string) => {
  await loadFetch(
    () =>
      store3.updateSmtpAccess({
        inboxId: inboxIdScope.value,
        updateSmtpAccessOptions: {
          smtpUsername: undefined,
          smtpPassword: newValue,
        },
      }),
    store3,
    loading,
  );
  await refresh();
  await store3.setFlash({
    text: 'SMTP password updated',
    variant: Variant.success,
  });
};
const onSmtpUsernameChange = async (newValue: string) => {
  await loadFetch(
    () =>
      store3.updateSmtpAccess({
        inboxId: inboxIdScope.value,
        updateSmtpAccessOptions: {
          smtpUsername: newValue,
          smtpPassword: undefined,
        },
      }),
    store3,
    loading,
  );
  await refresh();
  await store3.setFlash({
    text: 'SMTP username updated',
    variant: Variant.success,
  });
};
const onImapPasswordChange = async (newValue: string) => {
  await loadFetch(
    () =>
      store3.updateImapAccess({
        inboxId: inboxIdScope.value,
        updateImapAccessOptions: {
          imapUsername: undefined,
          imapPassword: newValue,
        },
      }),
    store3,
    loading,
  );
  await refresh();
  await store3.setFlash({
    text: 'IMAP password updated',
    variant: Variant.success,
  });
};
const onImapUsernameChange = async (newValue: string) => {
  await loadFetch(
    () =>
      store3.updateImapAccess({
        inboxId: inboxIdScope.value,
        updateImapAccessOptions: {
          imapUsername: newValue,
          imapPassword: undefined,
        },
      }),
    store3,
    loading,
  );
  await refresh();
  await store3.setFlash({
    text: 'IMAP username updated',
    variant: Variant.success,
  });
};

watch(
  () => inboxIdSelected.value,
  async () => {
    await refresh();
  },
);

watch(
  () => props.inboxId,
  async () => {
    await refresh();
  },
);
</script>
